'use client';
import { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { UseDisclosureReturn } from '@chakra-ui/react';
import { NewCreditModal } from '~/components/common/credits/NewCreditModal';
import { toaster } from '~/components/ui/toaster';
import { CREDIT_ERROR_TIPS } from '~/constants/errors/credit';
import { apiClient } from '~/lib/apiClient';
import { getGmoCreditToken } from '~/lib/payments/gmo_payment';
import { isArcApiError } from '~/src/services/errors';
import { RootContext } from '~/store';
import { CreditFormValue } from '~/types/components';

type Props = {
  disclosure: UseDisclosureReturn;
  setCardSeq: (card_seq: string) => void;
  mutate: () => void;
};

export const NewCreditModalContainer: React.FC<Props> = (props: Props) => {
  const { disclosure } = props;
  const { token } = useContext(RootContext);
  const creditForm = useForm<CreditFormValue>({
    mode: 'onSubmit',
  });
  const [submitting, setSubmitting] = useState(false);
  const [newCreditError, setNewCreditError] = useState<string>();
  const [creditErrorTips, setCreditErrorTips] = useState<string>();

  const reset = () => {
    creditForm.reset();
    setNewCreditError(undefined);
  };

  const handleSubmit = async (data: CreditFormValue) => {
    const isValid = await creditForm.trigger();
    if (!isValid) return;
    setNewCreditError(undefined);
    setCreditErrorTips(undefined);

    const card_number = (data.card_number ?? '').replace(/ /g, '');
    const security_code = (data.security_code ?? '').replace(/ /g, '');

    if (newCreditError) return;

    setSubmitting(true);

    const tokenRes = await getGmoCreditToken({
      cardno: card_number,
      holdername: data.cardholder_name,
      expire: `${data.card_expire_year}${data.card_expire_month}`,
      securitycode: security_code,
    });

    if (tokenRes.success == false) {
      setNewCreditError('このクレジットカードは登録できません');
      setSubmitting(false);
      return;
    }

    const creditPostRes = await apiClient.v1.me.credits.post({
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token?.token}`,
      },
      body: { token: tokenRes.tokenObj.token, version: 'v2' },
    });

    if (creditPostRes.status === 200) {
      if (creditPostRes.body?.cardSeq) {
        props.mutate();
        props.setCardSeq(creditPostRes.body.cardSeq);
        reset();
        disclosure.onClose();
        toaster.success({
          title: 'クレジットカードを登録しました',
          duration: 2000,
        });
      } else {
        setNewCreditError('このクレジットカードは登録できません');
      }
    } else {
      if (isArcApiError(creditPostRes.body)) {
        const errorTips = CREDIT_ERROR_TIPS[creditPostRes.body.code];

        setNewCreditError(creditPostRes.body.message);
        setCreditErrorTips(errorTips ?? '');
      } else {
        setNewCreditError('通信エラーです');
      }
    }

    setSubmitting(false);
  };

  return (
    <FormProvider {...creditForm}>
      <NewCreditModal
        disclosure={disclosure}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitErrorText={newCreditError}
        submitErrorTips={creditErrorTips}
      />
    </FormProvider>
  );
};
